<template>
  <div class="component-realtime-point">
    <!-- 标注点的容器 -->
    <ul class="mark-point-container" :style="containerStyle">
      <li
        v-for="(point, index) in transformedPointList"
        :key="index"
        :style="{left: point.posX + 'px', top: point.posY + 'px'}"
        :class="point.colorName"
      >
        <i class="circle"></i>
        <i class="solid"></i>
      </li>
    </ul>

    <aside :style="{width: showColorSelector ? '210px' : 'unset'}">
      <div class="fake-bg"></div>

      <ul class="operations">
        <li class="operation-btn" :class="[showColorSelector ? 'on' : '']">
          <button @click="toggleColorSelector"><svg-icon icon-class="drawn-line-color"></svg-icon></button>
          <span>{{$t('calling.Color')}}</span>
          <i></i>

          <ul v-show="showColorSelector" class="color-list">
            <li v-for="color in colorList" :key="color.name" :class="[color.selected ? 'selected' : '']">
              <i class="color-pointer" :class="[color.name]" @click="selectColor(color.name)"></i>
            </li>
          </ul>
        </li>

        <li class="operation-btn">
          <button @click="closeMark"><svg-icon icon-class="draw-close"></svg-icon></button>
          <span>{{$t('common.Close')}}</span>
        </li>
      </ul>
    </aside>
  </div>
</template>

<script>
import Global from '@/global'

let pointContainerEl

const colorValueMap = Object.fromEntries(Object.entries(Global.PUZZLE_COLOR_CODE).map(([key, value]) => [value, key]))
const colorList = ['blue', 'green', 'yellow', 'red', 'purple'].map((name, index) => ({
  name,
  color: Global.DRAW_COLOR_MAP[name],
  value: colorValueMap[name],
  selected: index === 0
}))

export default {
  name: 'RealtimePoint',

  props: {
    containerStyle: {
      type: Object,
      default: () => ({})
    },
    pointList: {
      type: Array,
      default: () => ([])
    }
  },

  data () {
    return {
      showColorSelector: false,
      colorList: colorList
    }
  },

  computed: {
    selectedColor () {
      return this.colorList.find(color => color.selected)
    },

    // 转换信令中的标注点数据，使之可以在容器中正常展示
    transformedPointList () {
      return this.pointList.map(point => {
        const { width: cssWidth, height: cssHeight } = pointContainerEl.getBoundingClientRect()
        return {
          posX: point.posx * cssWidth,
          posY: point.posy * cssHeight,
          colorName: Global.PUZZLE_COLOR_CODE[point.colour]
        }
      })
    }
  },

  mounted() {
    this.initContainerEl()
    this.bindContainerEvent()
  },

  methods: {
    initContainerEl () {
      pointContainerEl = document.querySelector('.mark-point-container')
    },

    // 点击时绘制标注点发送数据
    bindContainerEvent () {
      pointContainerEl.addEventListener('pointerdown', e => {
        const { offsetX, offsetY } = e
        const { width: cssWidth, height: cssHeight } = pointContainerEl.getBoundingClientRect()
        const ratioX = (offsetX < 0 ? 0 : offsetX > cssWidth ? cssWidth : offsetX) / cssWidth
        const ratioY = (offsetY < 0 ? 0 : offsetY > cssHeight ? cssHeight : offsetY) / cssHeight
        this.$emit('generate-mark', {
          posX: ratioX,
          posY: ratioY,
          color: this.selectedColor.value
        })
        e.stopPropagation()
        e.cancelBubble = true
      }, true)
    },

    // 切换颜色选择
    toggleColorSelector () {
      this.showColorSelector = !this.showColorSelector
      this.showWidthSelector = false
    },

    // 选中颜色
    selectColor (name) {
      this.colorList.forEach(color => (color.selected = color.name === name))
      this.showColorSelector = false
    },

    closeMark () {
      this.$emit('close-mark')
    }
  }
}
</script>

<style lang="scss" scoped>
.component-realtime-point {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  .mark-point-container {
    margin: 0;
    padding: 0;
    position: relative;
    border-radius: 16px;
    overflow: hidden;
    list-style: none;

    &::before {
      display: block;
      width: 100%;
      padding-top: 56.25%;
      content: '';
    }

    > li {
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: -1;

      i {
        display: block;
        position: absolute;
        width: 18px;
        height: 18px;
        border-radius: 50%;

        &.circle {
          animation: circle-fade 0.5s linear forwards;
        }

        &.solid {
          opacity: 0;
          animation: solid-fade 0.8s linear forwards;
        }
      }

      &.red {
        i.circle {
          background-color: #EB584166;
        }
        i.solid {
          background-color: #EB5841;
        }
      }

      &.yellow {
        i.circle {
          background-color: #F9DE4A66;
        }
        i.solid {
          background-color: #F9DE4A;
        }
      }

      &.green {
        i.circle {
          background-color: #6FDE8D66;
        }
        i.solid {
          background-color: #6FDE8D;
        }
      }

      &.blue {
        i.circle {
          background-color: #47B3FF66;
        }
        i.solid {
          background-color: #47B3FF;
        }
      }

      &.purple {
        i.circle {
          background-color: #736FDE66;
        }
        i.solid {
          background-color: #736FDE;
        }
      }
    }
  }

  > aside {
    height: 100%;
    position: absolute;
    right: 0;
    display: flex;
    align-items: center;

    .fake-bg {
      width: 120px;
      height: 272px;
      background-color: #FFFFFF;
      box-shadow: 0 5px 30px 0 rgba(89, 132, 219, 0.08);
      border-radius: 16px 0 0 16px;
      border: 1px solid #EDEFF2;
      border-right: none;
      position: absolute;
      right: 0;
    }

    .operations {
      height: 100%;
      margin: 0;
      padding: 0;
      position: absolute;
      right: 0;
      display: flex;
      flex-flow: column nowrap;
      justify-content: center;
      list-style: none;
      pointer-events: none;
      user-select: none;

      .operation-btn {
        width: 120px;
        margin-bottom: 24px;
        display: flex;
        flex-flow: column nowrap;
        align-items: center;
        position: relative;
        pointer-events: auto;

        &:last-child {
          margin-bottom: 0;
        }

        &.on {
          > button {
            color: #fff;
            background-color: #236CFF;

            &:hover {
              background-color: #236CFF;
            }
          }

          > i {
            background-color: #236CFF;
          }
        }

        > button {
          width: 56px;
          height: 56px;
          margin-bottom: 8px;
          padding: 0;
          font-size: 32px;
          background-color: #FFFFFF;
          box-shadow: 0 4px 6px 0 rgba(89, 132, 219, 0.08);
          border: 1px solid #EDEFF2;
          border-radius: 50%;
          cursor: pointer;
          color: #236CFF;

          &:hover {
            background-color: #F3F6FB;
          }
        }

        > span {
          line-height: 20px;
          font-size: 14px;
          color: #000000;
        }

        > i {
          display: block;
          width: 4px;
          height: 79px;
          background-color: #fff;
          position: absolute;
          top: 0;
          right: 0;
        }

        .color-list {
          width: 56px;
          padding: 0;
          margin: 0;
          position: absolute;
          top: 0;
          left: -76px;
          display: flex;
          flex-flow: column nowrap;
          list-style: none;

          > li {
            width: 56px;
            height: 56px;
            display: flex;
            justify-content: center;
            align-items: center;

            &::before {
              content: '';
              display: block;
              width: 100%;
              height: 100%;
              border-radius: 50%;
            }

            .color-pointer {
              width: 24px;
              height: 24px;
              position: absolute;
              box-shadow: 0 4px 6px 0 rgba(89, 132, 219, 0.08);
              border: 2px solid #FFFFFF;
              border-radius: 50%;
              cursor: pointer;

              &.blue {
                background-color: #47B3FF;
              }

              &.green {
                background-color: #6FDE8D;
              }

              &.yellow {
                background-color: #F9DE4A;
              }

              &.red {
                background-color: #EB5841;
              }

              &.purple {
                background-color: #736FDE;
              }
            }

            &.selected {
              &::before {
                background-color: #EBEEF3;
              }

              .color-pointer {
                width: 32px;
                height: 32px;
              }
            }
          }
        }
      }
    }
  }

  @keyframes circle-fade {
    0% {
      opacity: 1;
      transform: scale(1);
    }
    70% {
      opacity: 1;
      transform: scale(1.5);
    }
    100% {
      opacity: 0;
      transform: scale(1.5);
    }
  }

  @keyframes solid-fade {
    0%, 100% {
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
  }
}
</style>
